import React, { useContext, useEffect, useState } from "react";
import Header from "../../components/HeaderComponent";
import { AuthContext } from "../../components/AuthContextComponent";
import { AutoLogout } from "../../components/AutoLogoutComponent";
import { FormatNumber } from "../../lib/formatter/Formatter";
import {
  STATUS,
  TYPE,
  DATATYPE,
  ModalText,
  ROUTES,
} from "../../lib/enums/index";
import { useManualUpload, usePagination, useTheme } from "../../hooks/index";
import Modal from "../../components/modal/ModalComponent";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
} from "../../components/table/index";
import { headers, headers2 } from "../../lib/constants/index";
import Pagination from "../../components/pagination/Pagination";
import LoadingComponent from "../../components/loading/Loading";
import SecurityQuestion from "../../components/SecurityQuestion";
import { FaMagnifyingGlass } from "react-icons/fa6";
import PartnersListDropdown from "../../components/dropdown/PartnersListDropdown";
import UpdatePassword from "../../components/updatepassword/UpdatePassword";
import CurrencyDropdown from "../../components/dropdown/Dropdown";
import DownloadTemplateButton from "../../components/button/DownloadButton";
import useUserLogin from "../../hooks/UserLoginhook";
import useHeadComponent from "../../hooks/HeaderHook";
import TotalDisplay from "../../components/grid/TotalDisplay";
import { useLocation } from "react-router-dom";
import FileVersionList from "./components/FileVersionOptionList";

interface Props {}

const ManualUpload: React.FC<Props> = () => {
  const userInfo = useContext(AuthContext);

  const { username, setPartnerName } = useContext(AuthContext);

  const {
    selectedFile,
    jsonData,
    newJsonData,
    totalCharges,
    totalPrincipalAmount,
    total,
    currencies,
    selectedCurrency,
    partners,
    fileInputRef,
    selectedPartner,
    isValid,
    requestData,
    isConverting,
    openOTP,
    time,
    upload,
    requiredCurrency,
    requiredPartner,
    redirectModal,
    fetchCurrentUserQuestion,
    handleFileChange,
    handleButtonClick,
    handleSubmit,
    handlePartnerChange,
    handleCurrencyChange,
    setOpenOTP,
    resetFormState,
    fetchCurrencies,
    fetchPartners,
  } = useManualUpload();

  const location = useLocation();
  const { Balance } = useHeadComponent(selectedPartner);
  const [loading, setLoading] = useState(true);
  const { customColor } = useUserLogin();

  const [renderContent, setRenderContent] = useState<React.ReactNode>(null);
  const { pwdUpdate, category } = userInfo.userInfo || {};

  const redirectState = !!(redirectModal && category === "EXTERNAL");

  const firstTimeLoginState = !!(pwdUpdate && category === "EXTERNAL");

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasData()) {
        event.preventDefault();
        event.returnValue = "Leaving this page will cause the data to be lost.";
      }
    };

    const hasData = (): boolean => {
      return (
        jsonData.length > 0 ||
        selectedFile !== null ||
        selectedCurrency !== "" ||
        selectedPartner !== 0
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [jsonData, selectedFile, selectedCurrency, selectedPartner]);

  const sendType = userInfo.userInfo?.notification || "";
  const rows = newJsonData || [];

  const hasSndrBusinessName = rows.some(
    (row: any) => row.sndrBusinessName && row.sndrBusinessName.trim() !== "",
  );

  const { currentItems, currentPage, totalPages, handlePageChange } =
    usePagination(rows);

  const { theme } = useTheme();

  const fetchData = async () => {
    setLoading(true);
    try {
      setPartnerName("");

      if (userInfo.userInfo?.category === STATUS.INTERNAL) {
        await fetchPartners();
      }

      if (location.pathname === ROUTES.MANUALUPLOAD) {
        await fetchCurrencies();
      }

      setTimeout(() => {
        if (pwdUpdate && category === STATUS.EXTERNAL) {
          setRenderContent(<UpdatePassword />);
        } else if (redirectModal && category === STATUS.EXTERNAL) {
          setRenderContent(<SecurityQuestion />);
        } else {
          setRenderContent(null);
        }
      }, 500);
    } catch (error) {
      // Optional: handle error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchCurrentUserQuestion();
  }, [username, location.pathname, category]);

  return (
    <div>
      <Header
        Balance={Balance}
        theme={theme}
        selectedPartner={selectedPartner}
      ></Header>

      <AutoLogout
        openOTP={openOTP}
        redirectModal={redirectState}
        redirectChangePassword={firstTimeLoginState}
      />

      {loading ? (
        <>
          <div className="flex justify-center max-w-[1240px] mx-auto px-4 h-full items-center p-96">
            <LoadingComponent customColor={customColor} />
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-center w-full md:justify-start">
            <div className="flex flex-wrap justify-center w-screen gap-2 py-3 mx-auto font-sans md:justify-start md:px-10">
              <div
                className={`flex items-start justify-center lg:h-full h-14 xl:h-14`}
              >
                <CurrencyDropdown
                  label="Currency"
                  options={currencies}
                  isRequired={requiredCurrency}
                  onSelect={handleCurrencyChange}
                />
              </div>

              {/* Partner Dropdown (conditional) */}
              {userInfo?.userInfo?.category === STATUS.INTERNAL && (
                <div className="flex items-start py-2 lg:h-full h-14">
                  <PartnersListDropdown
                    label="Partner"
                    options={partners || []}
                    isRequired={requiredPartner}
                    onSelect={handlePartnerChange as any}
                  />
                </div>
              )}

              {/* File Selector */}
              <div className="flex flex-col items-start mt-1 ">
                <p
                  className={`pl-4 text-custom-micro p-1 ${
                    userInfo.userInfo?.theme === "DARK"
                      ? "text-custom-dark-theme-text"
                      : "text-black"
                  }`}
                >
                  File
                </p>

                <div
                  className="flex px-2 py-1 mb-2 ml-2 border border-gray-300 bg-white hover:bg-gray-100 h-[30px] rounded-md w-64 cursor-pointer"
                  onClick={handleButtonClick}
                >
                  <span className="flex items-center w-full overflow-hidden overflow-ellipsis whitespace-nowrap text-custom-petite">
                    {selectedFile ? selectedFile.name : "SELECT FILE"}
                  </span>
                </div>
              </div>

              {/* Browse Button */}
              <div
                className={`flex flex-col items-center justify-center px-4 pt-[20px]  ${
                  requiredCurrency || requiredPartner ? "pb-[19px]" : ""
                } `}
              >
                <button
                  onClick={handleButtonClick}
                  style={{ backgroundColor: customColor }}
                  className={`flex  items-center justify-center gap-2 px-4 py-2 h-[30px] w-[100px] rounded-md text-custom-micro bg-custom-default text-white ${
                    customColor
                      ? `hover:scale-105 transition-all duration-300`
                      : `hover:bg-custom-default-hover hover:shadow-lg   hover:shadow-red-500/40`
                  }" 
           `}
                >
                  <FaMagnifyingGlass /> Browse
                </button>

                <input
                  type={DATATYPE.FILE}
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>

              {/* button for donwload template */}
              <div
                className={`flex items-center justify-center pt-[20px] ${
                  requiredCurrency || requiredPartner ? "pb-[19px]" : ""
                }`}
              >
                {/* <DownloadTemplateButton /> */}
                <FileVersionList />
              </div>
            </div>
          </div>
        </>
      )}

      {isConverting ? (
        <div className="flex items-center justify-center w-screen h-[350px] mt-48">
          <LoadingComponent customColor={customColor} />
        </div>
      ) : (
        ""
      )}

      {upload ? (
        <>
          {" "}
          <div className="flex flex-wrap justify-center">
            <Table>
              <TableHeader>
                <TableRow
                  data={hasSndrBusinessName ? headers2 : headers}
                  isHeader={true}
                />
              </TableHeader>
              <TableBody>
                {currentItems.map((rowData: any, index: any) => (
                  <TableRow
                    key={index}
                    index={index}
                    data={
                      hasSndrBusinessName
                        ? [
                            rowData.transactionId,
                            rowData.refno,
                            // rowData.customerId,1
                            rowData.sndrBusinessName,
                            // rowData.sndrContactNo,
                            rowData.rcvrLName,
                            rowData.rcvrFName,
                            rowData.rcvrMName,
                            rowData.rcvrContactNo,
                            rowData.origCurrency,
                            rowData.origFee,
                            rowData.origPrincipalAmount,
                            rowData.destTotalAmount,
                            rowData.destCurrency,
                            rowData.destFee,
                            rowData.destPrincipalAmount,
                            rowData.destTotalAmount,
                          ]
                        : [
                            rowData.transactionId,
                            rowData.refno,
                            // rowData.customerId,
                            rowData.sndrLName,
                            rowData.sndrFName,
                            rowData.sndrMName,
                            rowData.sndrContactNo,
                            rowData.rcvrLName,
                            rowData.rcvrFName,
                            rowData.rcvrMName,
                            rowData.rcvrContactNo,
                            rowData.origCurrency,
                            rowData.origFee,
                            rowData.origPrincipalAmount,
                            rowData.destTotalAmount,
                            rowData.destCurrency,
                            rowData.destFee,
                            rowData.destPrincipalAmount,
                            rowData.destTotalAmount,
                          ]
                    }
                  />
                ))}
              </TableBody>
            </Table>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
          <div
            className={`flex flex-wrap justify-between ml-2 p-5 mt-10 ${
              userInfo.userInfo?.theme === "DARK" ? "text-white" : "text-black"
            }`}
          >
            <div className="px-4">
              <div className="flex flex-wrap-reverse justify-between font-sans text-custom-petite">
                <div></div>
                <div className="flex flex-col gap-1 md:gap-2 md:w-96">
                  <TotalDisplay
                    label="Total Principal :"
                    value={`${selectedCurrency}
                      ${
                        totalPrincipalAmount > 0
                          ? FormatNumber(totalPrincipalAmount)
                          : " 0.00"
                      }`}
                  />

                  <TotalDisplay
                    label="Total Charges :"
                    value={`${selectedCurrency}
                      ${
                        totalCharges > 0 ? FormatNumber(totalCharges) : " 0.00"
                      }`}
                  />

                  <TotalDisplay
                    label="Total :"
                    value={`${selectedCurrency}
                      ${total > 0 ? FormatNumber(total) : " 0.00"}`}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center px-4">
              <button
                onClick={handleSubmit}
                disabled={isValid === false}
                style={{ backgroundColor: customColor }}
                className={`px-4 py-2 w-[100px] h-[30px] text-white rounded bg-custom-default mt-5 text-custom-micro font-sans flex justify-center items-center ${
                  isValid ? "" : "opacity-60 cursor-not-allowed"
                } ${
                  customColor
                    ? `hover:scale-105 transition-all duration-300`
                    : `hover:bg-custom-default-hover hover:shadow-lg   hover:shadow-red-500/40`
                } `}
              >
                Submit
              </button>

              {openOTP ? (
                <Modal
                  footer="Didn't receive a code?"
                  type="OTP"
                  header="One Time Pin"
                  transType="dataOTP"
                  label={
                    sendType === TYPE.EMAIL
                      ? ModalText.OTPEmail
                      : ModalText.OTPSMS
                  }
                  data={requestData}
                  isClose={false}
                  setOpenOTP={setOpenOTP}
                  userId={username}
                  otpTimer={time}
                  otpType="TRANSACTION"
                  reset={resetFormState}
                  sendType={sendType}
                  toggleClose={() => handleSubmit(false)}
                />
              ) : (
                " "
              )}
            </div>
          </div>
        </>
      ) : (
        ""
      )}

      {renderContent}
    </div>
  );
};

export default ManualUpload;
